import { useState } from 'react'
import { Outlet } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

import { useCompanySettings } from '@/hooks/useCompanySettings'
import { useUiStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

/**
 * The application frame: sidebar + top bar, matching design/dashboard.png.
 *
 * Below 1024px the sidebar becomes an overlay drawer rather than shrinking the content
 * area, which is what keeps the dashboard's 12-column grid usable on a tablet.
 */
export function AppShell() {
  const { t } = useTranslation()
  const { sidebarCollapsed, toggleSidebar } = useUiStore()
  const [mobileOpen, setMobileOpen] = useState(false)

  // The drawer closes via the Sidebar's onNavigate callback rather than an effect on
  // pathname: closing it is a consequence of the click, not of the URL changing, and
  // setState inside an effect would cause a second render pass on every navigation.

  // Shown rather than papered over: the old fallback rendered Bangladeshi taka against
  // dirham figures on a UAE deployment, and nothing said it was a guess.
  const { unavailable: localizationUnavailable } = useCompanySettings()

  return (
    <div className="flex h-dvh overflow-hidden bg-[var(--color-page)]">
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar collapsed={sidebarCollapsed} />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed start-0 top-0 z-50 h-dvh lg:hidden">
            <Sidebar collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </div>
        </>
      )}

      {/* min-h-0 so the column may be shorter than its content: without it a flex item
          floors at its content height and `main` grows past the viewport, which is what
          let the page scroll on past the end of a panel into empty space. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Topbar
          onToggleSidebar={() => {
            if (window.matchMedia('(max-width: 1023px)').matches) {
              setMobileOpen((open) => !open)
            } else {
              toggleSidebar()
            }
          }}
        />

        {localizationUnavailable && (
          <p
            role="alert"
            className="border-b border-[var(--color-warning)] bg-[var(--color-tint-orange)] px-4 py-2 text-sm text-[var(--color-heading)] lg:px-6"
          >
            Localisation not loaded: the company's currency, date format and timezone could not
            be read, so figures are shown without a currency symbol and dates in ISO form.
          </p>
        )}

        <main
          id="main-content"
          tabIndex={-1}
          // overscroll-contain: a scroll that reaches the end of this area stops there
          // rather than handing the remainder to the document behind it.
          // `relative` so an absolutely positioned descendant — a screen-reader live
          // region, say — is contained and clipped here. Without it the containing block is
          // the page itself, and a 1px sr-only paragraph sitting at the end of a long panel
          // stretches the document by however far down the panel it happens to fall, which
          // is exactly what made the whole page scrollable behind a shell that is supposed
          // to be exactly the viewport.
          className={cn('relative min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 lg:p-6')}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
